'use client';
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { BeforeAfterItem, ReviewItem } from '@/app/admin/components/ReviewsManager';

function dbRowToPhoto(row: Record<string, unknown>): BeforeAfterItem {
  return {
    id: row.id as string,
    label: (row.label as string) ?? '',
    package: (row.package as string) ?? '',
    before: (row.before_url as string) ?? '',
    after: (row.after_url as string) ?? '',
    beforeAlt: (row.before_alt as string) ?? '',
    afterAlt: (row.after_alt as string) ?? '',
    stat: (row.stat as string) ?? '',
  };
}

function dbRowToReview(row: Record<string, unknown>): ReviewItem {
  return {
    id: row.id as string,
    name: (row.name as string) ?? '',
    location: (row.location as string) ?? '',
    rating: (row.rating as number) ?? 5,
    text: (row.review_text as string) ?? '',
    package: (row.package as string) ?? '',
    date: (row.date_label as string) ?? '',
  };
}

interface ResultsPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ResultsPopup({ isOpen, onClose }: ResultsPopupProps) {
  const [activeTab, setActiveTab] = useState<'results' | 'reviews'>('results');
  const [activeItem, setActiveItem] = useState(0);
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [beforeAfterItems, setBeforeAfterItems] = useState<BeforeAfterItem[]>([]);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      document.body.style.overflow = 'hidden';
      setActiveItem(0);

      // Fetch latest data from Supabase each time popup opens
      const fetchData = async () => {
        setLoading(true);
        try {
          const [photosRes, reviewsRes] = await Promise.all([
            supabase.from('before_after_photos').select('*').order('sort_order', { ascending: true }),
            supabase.from('reviews').select('*').eq('is_approved', true).order('sort_order', { ascending: true }).order('created_at', { ascending: false }),
          ]);
          if (!photosRes.error && photosRes.data) {
            setBeforeAfterItems(photosRes.data.map(dbRowToPhoto));
          }
          if (!reviewsRes.error && reviewsRes.data) {
            setReviews(reviewsRes.data.map(dbRowToReview));
          }
        } catch {
          // silently fail — empty state will show
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSliderMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const pos = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100));
    setSliderPos(pos);
  };

  if (!mounted && !isOpen) return null;

  const currentItem = beforeAfterItems[activeItem] ?? beforeAfterItems[0];
  const fiveStarCount = reviews.filter((r) => r.rating === 5).length;
  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '5.0';

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(5,5,16,0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 9998,
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 0.35s ease',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      />

      {/* Slide-out panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(680px, 100vw)',
          background: '#050510',
          borderLeft: '1px solid rgba(0,245,255,0.25)',
          boxShadow: '-20px 0 80px rgba(0,245,255,0.08), -4px 0 40px rgba(123,47,255,0.1)',
          zIndex: 9999,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Scanline overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,245,255,0.012) 2px, rgba(0,245,255,0.012) 4px)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        {/* Top accent line */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, #00f5ff, #7b2fff, #ff00ff)', boxShadow: '0 0 12px rgba(0,245,255,0.5)', zIndex: 2 }} />

        {/* Header */}
        <div
          style={{
            background: '#0d0d2b',
            borderBottom: '1px solid rgba(0,245,255,0.15)',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div>
            <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#00f5ff', textShadow: '0 0 8px rgba(0,245,255,0.5)', marginBottom: '2px' }}>
              Vertex Premium
            </p>
            <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: '22px', textTransform: 'uppercase', color: '#e0e8ff', letterSpacing: '0.02em' }}>
              Results & Reviews
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#111130',
              border: '1px solid rgba(0,245,255,0.2)',
              color: '#a0b0d8',
              borderRadius: '50%',
              width: '38px',
              height: '38px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '20px',
              fontWeight: 900,
              transition: 'all 0.15s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00f5ff'; e.currentTarget.style.color = '#00f5ff'; e.currentTarget.style.boxShadow = '0 0 12px rgba(0,245,255,0.3)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(0,245,255,0.2)'; e.currentTarget.style.color = '#a0b0d8'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            ×
          </button>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', background: '#050510', borderBottom: '1px solid rgba(0,245,255,0.1)', flexShrink: 0, position: 'relative', zIndex: 2 }}>
          {(['results', 'reviews'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '14px 16px',
                background: activeTab === tab ? 'rgba(0,245,255,0.06)' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #00f5ff' : '2px solid transparent',
                cursor: 'pointer',
                fontFamily: 'Barlow Condensed, Arial, sans-serif',
                fontWeight: 900,
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: activeTab === tab ? '#00f5ff' : '#7b8ab8',
                transition: 'all 0.2s ease',
              }}
            >
              {tab === 'results' ? '◈ Before / After' : '★ Reviews'}
            </button>
          ))}
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', position: 'relative', zIndex: 2 }}>

          {/* Loading state */}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.15em', color: 'rgba(0,245,255,0.5)' }}>
                ◌ Loading…
              </span>
            </div>
          )}

          {/* ── Results Tab ── */}
          {!loading && activeTab === 'results' && (
            <div style={{ padding: '20px' }}>
              {beforeAfterItems.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center', gap: 12 }}>
                  <span style={{ fontSize: 40, opacity: 0.3 }}>◈</span>
                  <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: 18, textTransform: 'uppercase', color: '#7b8ab8', letterSpacing: '0.05em' }}>
                    No Results Yet
                  </p>
                  <p style={{ fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(168,169,173,0.35)', maxWidth: 260, lineHeight: 1.6 }}>
                    Before & after photos will appear here once added by the admin.
                  </p>
                </div>
              ) : (
                <>
                  {/* Thumbnail strip */}
                  {beforeAfterItems.length > 1 && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
                      {beforeAfterItems.map((item, idx) => (
                        <button
                          key={item.id}
                          onClick={() => { setActiveItem(idx); setSliderPos(50); }}
                          style={{
                            flexShrink: 0,
                            width: 72,
                            height: 52,
                            borderRadius: 6,
                            overflow: 'hidden',
                            border: activeItem === idx ? '2px solid #00f5ff' : '2px solid rgba(255,255,255,0.08)',
                            cursor: 'pointer',
                            padding: 0,
                            background: 'none',
                            boxShadow: activeItem === idx ? '0 0 10px rgba(0,245,255,0.4)' : 'none',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <img src={item.after} alt={item.afterAlt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Label */}
                  {currentItem && (
                    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: 16, textTransform: 'uppercase', color: '#e0e8ff', letterSpacing: '0.04em' }}>
                        {currentItem.label}
                      </span>
                      {currentItem.package && (
                        <span style={{ fontFamily: 'monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#7b2fff', background: 'rgba(123,47,255,0.12)', border: '1px solid rgba(123,47,255,0.3)', padding: '2px 8px', borderRadius: 20 }}>
                          {currentItem.package}
                        </span>
                      )}
                      {currentItem.stat && (
                        <span style={{ fontFamily: 'monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#00f5ff', background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.25)', padding: '2px 8px', borderRadius: 20 }}>
                          {currentItem.stat}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Drag-to-compare slider */}
                  {currentItem && (
                    <div
                      style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 12, overflow: 'hidden', cursor: 'ew-resize', userSelect: 'none', border: '1px solid rgba(0,245,255,0.15)' }}
                      onMouseDown={() => setIsDragging(true)}
                      onMouseUp={() => setIsDragging(false)}
                      onMouseLeave={() => setIsDragging(false)}
                      onMouseMove={handleSliderMove}
                      onTouchStart={() => setIsDragging(true)}
                      onTouchEnd={() => setIsDragging(false)}
                      onTouchMove={handleSliderMove}
                    >
                      {/* After (full) */}
                      <img src={currentItem.after} alt={currentItem.afterAlt} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
                      {/* Before (clipped) */}
                      <div style={{ position: 'absolute', inset: 0, width: `${sliderPos}%`, overflow: 'hidden' }}>
                        <img src={currentItem.before} alt={currentItem.beforeAlt} style={{ position: 'absolute', inset: 0, width: `${100 / (sliderPos / 100)}%`, maxWidth: 'none', height: '100%', objectFit: 'cover' }} draggable={false} />
                      </div>
                      {/* Divider */}
                      <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${sliderPos}%`, width: 2, background: '#00f5ff', boxShadow: '0 0 12px rgba(0,245,255,0.8)', transform: 'translateX(-50%)' }}>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 32, height: 32, borderRadius: '50%', background: '#050510', border: '2px solid #00f5ff', boxShadow: '0 0 16px rgba(0,245,255,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#00f5ff' }}>⇔</div>
                      </div>
                      {/* Labels */}
                      <span style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(5,5,16,0.85)', border: '1px solid rgba(255,0,255,0.5)', padding: '3px 10px', fontFamily: 'monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#ff00ff' }}>Before</span>
                      <span style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(5,5,16,0.85)', border: '1px solid rgba(0,245,255,0.5)', padding: '3px 10px', fontFamily: 'monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#00f5ff' }}>After</span>
                    </div>
                  )}

                  <p style={{ marginTop: 10, fontFamily: 'monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(168,169,173,0.4)', textAlign: 'center' }}>
                    ← Drag to compare →
                  </p>
                </>
              )}
            </div>
          )}

          {/* ── Reviews Tab ── */}
          {!loading && activeTab === 'reviews' && (
            <div style={{ padding: '20px' }}>
              {reviews.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center', gap: 12 }}>
                  <span style={{ fontSize: 40, opacity: 0.3 }}>★</span>
                  <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: 18, textTransform: 'uppercase', color: '#7b8ab8', letterSpacing: '0.05em' }}>
                    No Reviews Yet
                  </p>
                  <p style={{ fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(168,169,173,0.35)', maxWidth: 260, lineHeight: 1.6 }}>
                    Customer reviews will appear here once added by the admin.
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div style={{ background: 'linear-gradient(135deg, rgba(0,245,255,0.06) 0%, rgba(123,47,255,0.06) 100%)', border: '1px solid rgba(0,245,255,0.15)', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: 40, color: '#00f5ff', textShadow: '0 0 20px rgba(0,245,255,0.5)', lineHeight: 1 }}>{avgRating}</p>
                      <div style={{ display: 'flex', gap: 2, justifyContent: 'center', marginTop: 4 }}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <span key={s} style={{ color: '#00f5ff', fontSize: 14, textShadow: '0 0 6px rgba(0,245,255,0.5)' }}>★</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: 16, textTransform: 'uppercase', color: '#e0e8ff', letterSpacing: '0.04em' }}>
                        {reviews.length} Customer {reviews.length === 1 ? 'Review' : 'Reviews'}
                      </p>
                      <p style={{ fontFamily: 'monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(168,169,173,0.5)', marginTop: 2 }}>
                        {fiveStarCount} five-star {fiveStarCount === 1 ? 'rating' : 'ratings'}
                      </p>
                    </div>
                  </div>

                  {/* Review cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.02) 0%, rgba(123,47,255,0.04) 100%)', border: '1px solid rgba(123,47,255,0.15)', borderRadius: 12, padding: '16px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
                          <div>
                            <p style={{ fontFamily: 'Barlow Condensed, Arial, sans-serif', fontWeight: 900, fontSize: 15, color: '#e0e8ff', letterSpacing: '0.02em' }}>{review.name}</p>
                            {review.location && (
                              <p style={{ fontFamily: 'monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(168,169,173,0.5)', marginTop: 1 }}>📍 {review.location}</p>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                            {Array.from({ length: review.rating }).map((_, i) => (
                              <span key={i} style={{ color: '#00f5ff', fontSize: 13, textShadow: '0 0 6px rgba(0,245,255,0.5)' }}>★</span>
                            ))}
                          </div>
                        </div>
                        <p style={{ fontSize: 13, color: '#a0b0d8', lineHeight: 1.65, fontStyle: 'italic', marginBottom: 10 }}>"{review.text}"</p>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {review.package && (
                            <span style={{ fontFamily: 'monospace', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#7b2fff', background: 'rgba(123,47,255,0.1)', border: '1px solid rgba(123,47,255,0.25)', padding: '2px 8px', borderRadius: 20 }}>{review.package}</span>
                          )}
                          {review.date && (
                            <span style={{ fontFamily: 'monospace', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(0,245,255,0.6)', background: 'rgba(0,245,255,0.05)', border: '1px solid rgba(0,245,255,0.15)', padding: '2px 8px', borderRadius: 20 }}>{review.date}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
